import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Notification } from '../interfaces/Notifications';

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (n: Notification) => void;
  markAllAsRead: () => void;
  setUserId: (userId: string) => void;
  hasUnread: boolean;
  setHasUnread: (value: boolean) => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const NotificationProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const [userId, setUserId] = useState<string>('');
  const [hasUnread, setHasUnread] = useState(false);
  const addNotification = (notification: Notification) => {
    setNotifications((prev) => [notification, ...prev]);
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setHasUnread(false);
  };

  useEffect(() => {
    if (!userId) return;
    const WS_URL = `wss://gqsdnbhx7l.execute-api.eu-west-3.amazonaws.com/production?userId=${userId}`;
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('🔌 WebSocket connected');
    };

    ws.onmessage = (event) => {
      try {
        const data: Notification = JSON.parse(event.data);
        console.log('📥 Notification received:', data);
        addNotification(data);
        setHasUnread(true);
      } catch (error) {
        console.error('❌ Error parsing notification:', error);
      }
    };

    ws.onclose = () => {
      console.log('🔌 WebSocket closed');
    };

    return () => {
      ws.close();
    };
  }, [userId]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        markAllAsRead,
        setUserId,
        hasUnread,
        setHasUnread,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context)
    throw new Error(
      'useNotificationContext must be used within NotificationProvider',
    );
  return context;
};
